import { Link } from "react-router-dom";
import { useSiteContent, useSiteFlag } from "@/context/SiteContentContext";
import { useT } from "@/i18n";
import { SCHEMES } from "@/lib/schemes";

// Map each scheme name to a solid bg+fg pair for the (very thin) announce bar,
// so it stays consistent with the section schemes admin can pick from.
// Falls back to the cyan default when no scheme is set.
const ANNOUNCE_SWATCH = {
    cream:    ["#F5F5F5", "#111111"],
    white:    ["#FFFFFF", "#111111"],
    gray:     ["#DFDFDF", "#111111"],
    sand:     ["#F0E4D0", "#2B2118"],
    mint:     ["#DFE9DD", "#1F2A22"],
    charcoal: ["#242322", "#F0C56C"],
    purple:   ["#5D3FD3", "#FFFFFF"],
    black:    ["#0E0E0E", "#00E0FF"],
};

// Top announcement bar — classyoutlaw-style thin strip above the header.
// Toggleable via `section_show_announce`. Copy per-locale via
// `announce_text_en` / `announce_text_es`. Optional link via `announce_link`.
// Marquee behavior when text is long: enable `announce_marquee=1`.
// Color: picks from the section_scheme_announce preset (falls back to cyan),
// with an optional raw-hex override via announce_bg_hex/announce_fg_hex.
export const AnnounceBar = () => {
    const { lang } = useT();
    const { content } = useSiteContent();
    const show = useSiteFlag("section_show_announce", false);
    const text = content[`announce_text_${lang}`] || content.announce_text_en || "";
    const link = content.announce_link || "";
    const marquee = content.announce_marquee === "1" || content.announce_marquee === true;
    if (!show || !text.trim()) return null;

    const inner = marquee ? (
        <div className="overflow-hidden">
            <div className="whitespace-nowrap animate-marquee inline-block will-change-transform">
                <span className="mx-8">{text}</span>
                <span className="mx-8">·</span>
                <span className="mx-8">{text}</span>
                <span className="mx-8">·</span>
                <span className="mx-8">{text}</span>
                <span className="mx-8">·</span>
                <span className="mx-8">{text}</span>
            </div>
        </div>
    ) : (
        <div className="text-center truncate">{text}</div>
    );

    // Resolve colors: raw hex takes priority, then scheme, then cyan fallback.
    const schemeKey = (content.section_scheme_announce || "").toLowerCase();
    const schemeSwatch = ANNOUNCE_SWATCH[schemeKey] || ANNOUNCE_SWATCH[schemeKey === "default" ? "cream" : ""] || ["#00E0FF", "#0E0E0E"];
    const bgHex = (content.announce_bg_hex || schemeSwatch[0]).trim();
    const fgHex = (content.announce_fg_hex || schemeSwatch[1]).trim();

    // Admin-editable size — 100 = default. 50 = half height, 200 = double.
    const sizePct = Math.max(30, Math.min(300, parseInt(content.announce_size_pct || "100", 10) || 100));
    const baseFontPx = 10 * (sizePct / 100);
    const basePadY = Math.max(2, Math.round(4 * (sizePct / 100)));
    const cls = "font-heading font-bold uppercase tracking-[0.2em] px-3";
    const style = {
        backgroundColor: bgHex,
        color: fgHex,
        fontSize: `${baseFontPx}px`,
        paddingTop: `${basePadY}px`,
        paddingBottom: `${basePadY}px`,
    };

    // Reference SCHEMES so tree-shaking keeps it in the bundle (chip previews rely on it).
    void SCHEMES;

    if (link && link.startsWith("/")) {
        return <Link to={link} data-testid="announce-bar" className={`block ${cls}`} style={style}>{inner}</Link>;
    }
    if (link) {
        return <a href={link} target="_blank" rel="noreferrer" data-testid="announce-bar" className={`block ${cls}`} style={style}>{inner}</a>;
    }
    return <div data-testid="announce-bar" className={cls} style={style}>{inner}</div>;
};

export default AnnounceBar;
